// ============================================
// CENTRALIZED SELECTORS
// ============================================
// This file exports all selectors used throughout the application.
// Provider-specific selectors come from providerInterface.js, which
// accesses state.game and state.spin (populated by store.js based on provider).
// Shared selectors are exported directly from their respective slices.

// ============================================
// PROVIDER-SPECIFIC SELECTORS
// ============================================
// These work with any provider because store.js loads the correct slice
export {
  selectIsSpinning,
  selectSpinComplete,
  selectManualStop,
  selectSessionExpired,
  selectCurrentRound,
  selectInitialReels,
  selectWinningLines,
  selectBalance,
  selectBet,
  selectPaytable,
  selectCurrency,
  selectCurrencyConfig,
  selectBonusBuyMultiplier,
  selectDisplayedBalance,
  selectBets,
  selectGameStatus,
  selectAssetsStatus,
  selectAvailableActions,
  selectFlowState,
  selectFeatures,
  selectLastCommand,
  selectIsResumingHoldAndWin,
} from "./Slices/providerInterface";

// Feature game selectors
export const selectHoldAndWinActive = (state) =>
  state.featureGame.holdAndWinActive;
export const selectHoldAndWinInitialReels = (state) =>
  state.featureGame.initialReels;
export const selectHoldAndWinCurrentRound = (state) =>
  state.featureGame.currentRound;
export const selectHoldAndWinCoinMultipliers = (state) =>
  state.featureGame.currentRound?.storage?.coin_multipliers || null;
export const selectRespinsLeft = (state) => state.featureGame.respinsLeft;
export const selectProgressBarCount = (state) =>
  state.featureGame.progressBarCount;
export const selectHoldAndWinSymbolsReady = (state) =>
  state.featureGame.holdAndWinSymbolsReady;
export const selectHoldAndWinReelsReady = (state) =>
  state.featureGame.holdAndWinReelsReady;
export const selectHoldAndWinOverlayReelsReady = (state) =>
  state.featureGame.holdAndWinOverlayReelsReady;
export const selectIsHoldAndWinIntroReload = (state) =>
  state.featureGame.isHoldAndWinIntroReload;
export const selectIsHoldAndWinMidFeatureReload = (state) =>
  state.featureGame.isHoldAndWinMidFeatureReload;

// HoldAndWin spin state selectors
export const selectHoldAndWinIsSpinning = (state) =>
  state.spin.holdAndWinIsSpinning;
export const selectHoldAndWinSpinComplete = (state) =>
  state.spin.holdAndWinSpinComplete;
export const selectHoldAndWinStatus = (state) => state.spin.holdAndWinStatus;
export const selectHoldAndWinManualStop = (state) =>
  state.spin.holdAndWinManualStop;

// ============================================
// USER INTERFACE SELECTORS
// ============================================
export {
  selectMenuActive,
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectPaytableActive,
  selectGameRulesActive,
  selectSettingsActive,
  selectCreditsPopupActive,
  selectInsufficientCreditsPopupActive,
  selectExpirationPopupActive,
  selectBuyFreeSpinsPopupActive,
  selectConnectionPopupActive,
  selectSoundAdjustmentPopupActive,
  selectSymbolPopupActive,
  selectSymbolPopupBlocked,
  selectTurboSuggestionPopupActive,
  selectAutoplayPopupActive,
  selectBetSelectPopupActive,
  selectBonusBuyActive,
  selectSpacebarSpinEnabled,
  selectAnyPopupActive,
  selectHoldAndWinResumePopupActive,
  selectErrorPopupActive,
  selectErrorPopupCode,
} from "./Slices/userInterfaceSlice";

// ============================================
// SETTINGS SELECTORS
// ============================================
export {
  selectAutoPlaySpeed,
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectAutoSpinCount,
  selectPendingAutoPlaySpeed,
  selectSimulationModeEnabled,
  selectBuyFreeSpinsMode,
  selectSoundLevelAdjustmentEnabled,
  selectManualStopCount,
  selectManualStopTrackingEnabled,
  selectTurboSuggestionPending,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
} from "./Slices/settingsSlice";

// ============================================
// ANIMATION SELECTORS
// ============================================
export {
  selectIntroTransitionActive,
  selectShowGameAfterTransition,
  selectResetMainGameVisualEffects,
  selectCounterAnimationFinished,
  selectLinesRotationFinished,
  selectWinAnimationsPlaying,
  selectCriticalAnimationPlaying,
  selectUserWantsToDismissWinAnimation,
  selectSkipWinAnimation,
  selectIsWaitingForUserConfirmation,
  selectAnimatedWinAmount,
  selectCounterAnimationStarted,
  selectSkipCounterAnimation,
  selectShowHoldAndWinIntro,
  selectSceneTransitionActive,
  selectShowHoldAndWinOutro,
  selectShowHoldAndWinCollector,
  selectShowMaxWin,
  selectExpandingWildsOccurred,
} from "./Slices/animationsSlice";

// ============================================
// SOUND SELECTORS
// ============================================
export { selectMasterVolume } from "./Slices/soundsSlice";

// ============================================
// FREE SPIN SELECTORS
// ============================================
export {
  selectFreeSpinsActive,
  selectIsPlayingRound,
  selectIsLastFreeSpinRound,
  selectFreeSpinsComplete,
  selectTotalFreeSpinWins,
  selectTotalWin,
  selectCurrentFreeSpinRound,
  selectSymbolsRevealed,
  selectTotalWinForOutro,
} from "./Slices/freeSpinsSlice";

// ============================================
// FREE SPIN ANIMATION SELECTORS
// ============================================
export {
  selectShowFreeSpinTransition,
  selectShowFreeSpinIntro,
  selectShowFreeSpinRetrigger,
  selectShowFreeSpinOutro,
  selectIsFreeSpinDimActive,
  selectShowReelDecoration,
} from "./Slices/freeSpinAnimationsSlice";
